import { useAtom, useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { EmptySearchResults, Pagination, SkeletonText } from '@ekhodealer/ekho-common/rev';

import { Table, type TableColumn } from '@common/components/Table/Table';
import { formatNumberWithCommas } from '@common/lib/currency';
import { getPaymentStatusTag } from '@common/lib/reports';
import { capitalizeFirstLetter } from '@common/lib/strings';

import {
	currentPageAtom,
	isGeneratingPaymentReconciliationReportAtom,
	itemsPerPageAtom,
	paymentReconciliationReportDataAtom,
	type PaymentReconciliationReportItem,
} from '../../atoms/paymentReconciliationAtoms';

import './styles.css';

// Mapped payment type for table display
interface Payment {
	id: string;
	customer: string;
	orderNumber: number | null;
	transferDate: Date | null;
	paidAt: Date | null;
	transferAmount: number;
	processingFees: number; // Stripe fees
	ekhoFee: number;
	transferPlatform: string;
	receiptType: string;
	vehicleId: string; // uid
	transferId: string;
	recipientName: string;
	recipientId: string;
	receiptStatus: string;
	transferStatus: string;
	receiptId: string;
	placedOrderId: string;
	formattedVin: string;
}

// Map backend data to table format
const mapReportItemToPayment = (item: PaymentReconciliationReportItem): Payment => {
	const transferAmount = parseFloat(item.transferAmount || '0');
	const processingFees = parseFloat(item.processingFees || '0');
	const ekhoFee = parseFloat(item.ekhoFee || '0');

	const transferDateValue = item.transferDate
		? typeof item.transferDate === 'string'
			? parseInt(item.transferDate, 10)
			: item.transferDate
		: null;
	const transferDate = transferDateValue ? new Date(transferDateValue) : null;

	const paidAtValue = item.paidAt
		? typeof item.paidAt === 'string'
			? parseInt(item.paidAt, 10)
			: item.paidAt
		: null;
	const paidAt = paidAtValue ? new Date(paidAtValue) : null;

	return {
		id: item.receiptId,
		customer: item.customer || 'Unknown',
		orderNumber: item.orderNumber ?? null,
		transferDate,
		paidAt,
		transferAmount,
		processingFees,
		ekhoFee,
		transferPlatform: item.transferPlatform || 'N/A',
		receiptType: item.receiptType || 'N/A',
		vehicleId: item.uid || 'N/A',
		transferId: item.transferId || 'N/A',
		recipientName: item.recipientName || 'N/A',
		recipientId: item.recipientId || 'N/A',
		receiptStatus: item.receiptStatus || 'N/A',
		transferStatus: item.transferStatus || 'N/A',
		receiptId: item.receiptId,
		placedOrderId: item.placedOrderId || 'N/A',
		formattedVin: item.formattedVin || 'N/A',
	};
};

const columns: TableColumn<Payment>[] = [
	{
		id: 'customer',
		title: 'Customer',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.customer}</div>
		),
	},
	{
		id: 'orderNumber',
		title: 'Order #',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				{item.orderNumber != null ? item.orderNumber : '--'}
			</div>
		),
	},
	{
		id: 'transferDate',
		title: 'Transfer date',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				{item.transferDate
					? item.transferDate.toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
					})
					: '--'}
			</div>
		),
	},
	{
		id: 'paidAt',
		title: 'Paid at',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				{item.paidAt
					? item.paidAt.toLocaleDateString('en-US', {
						year: 'numeric',
						month: 'short',
						day: 'numeric',
					})
					: '--'}
			</div>
		),
	},
	{
		id: 'transferAmount',
		title: 'Transfer amount',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatNumberWithCommas(item.transferAmount)}
			</div>
		),
	},
	{
		id: 'processingFees',
		title: 'Stripe fees',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatNumberWithCommas(item.processingFees)}
			</div>
		),
	},
	{
		id: 'ekhoFee',
		title: 'Ekho fee',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>
				${formatNumberWithCommas(item.ekhoFee)}
			</div>
		),
	},
	{
		id: 'transferPlatform',
		title: 'Transfer platform',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.transferPlatform}</div>
		),
	},
	{
		id: 'receiptType',
		title: 'Receipt type',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.receiptType}</div>
		),
	},
	{
		id: 'vehicleId',
		title: 'Vehicle ID',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.vehicleId}</div>
		),
	},
	{
		id: 'transferId',
		title: 'Transfer ID',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.transferId}</div>
		),
	},
	{
		id: 'recipientName',
		title: 'Recipient name',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontWeight: 400 }}>{item.recipientName}</div>
		),
	},
	{
		id: 'recipientId',
		title: 'Recipient ID',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.recipientId}</div>
		),
	},
	{
		id: 'receiptStatus',
		title: 'Receipt status',
		renderCell: (item: Payment) => {
			const capitalizedStatus = capitalizeFirstLetter(item.receiptStatus);
			return getPaymentStatusTag(capitalizedStatus);
		},
	},
	{
		id: 'transferStatus',
		title: 'Transfer status',
		renderCell: (item: Payment) => {
			const capitalizedStatus = capitalizeFirstLetter(item.transferStatus);
			return getPaymentStatusTag(capitalizedStatus);
		},
	},
	{
		id: 'receiptId',
		title: 'Receipt ID',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.receiptId}</div>
		),
	},
	{
		id: 'placedOrderId',
		title: 'Placed order ID',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.placedOrderId}</div>
		),
	},
	{
		id: 'formattedVin',
		title: 'Formatted VIN',
		renderCell: (item: Payment) => (
			<div style={{ fontSize: '13px', fontFamily: 'monospace' }}>{item.formattedVin}</div>
		),
	},
];

function ReportsPaymentReconciliationTable() {
	const [currentPage, setCurrentPage] = useAtom(currentPageAtom);
	const [itemsPerPage, setItemsPerPage] = useAtom(itemsPerPageAtom);
	const reportData = useAtomValue(paymentReconciliationReportDataAtom);
	const isPending = useAtomValue(isGeneratingPaymentReconciliationReportAtom);

	// Map report data to payment format
	const payments = useMemo(() => {
		if (!reportData) return [];
		return reportData.map(mapReportItemToPayment);
	}, [reportData]);

	const sortedPayments = useMemo(() => {
		return [...payments].sort((a, b) => {
			// Sort by transferDate, falling back to receiptId if transferDate is null
			const aTime = a.transferDate ? a.transferDate.getTime() : 0;
			const bTime = b.transferDate ? b.transferDate.getTime() : 0;
			if (aTime !== bTime) {
				return bTime - aTime; // Most recent first
			}
			return b.receiptId.localeCompare(a.receiptId); // Fallback sort by receiptId
		});
	}, [payments]);

	const paginatedPayments = useMemo(() => {
		const startIndex = (currentPage - 1) * itemsPerPage;
		const endIndex = startIndex + itemsPerPage;
		return sortedPayments.slice(startIndex, endIndex);
	}, [sortedPayments, currentPage, itemsPerPage]);

	const handlePageChange = (page: number) => {
		setCurrentPage(page);
	};

	const handleItemsPerPageChange = (newItemsPerPage: number) => {
		setItemsPerPage(newItemsPerPage);
		setCurrentPage(1);
	};

	// Show skeleton when loading - always show skeleton during loading regardless of previous data
	if (isPending) {
		// Create skeleton table rows
		const skeletonRows: Array<{ id: string }> = Array.from({ length: 5 }, (_, index) => ({
			id: `skeleton-${index}`,
		}));

		const skeletonColumns: TableColumn<{ id: string }>[] = columns.map((col) => ({
			...col,
			renderCell: () => <SkeletonText numberOfLines={1} textSize='body' />,
		}));

		return (
			<Table
				className='w-full payments-table'
				columns={skeletonColumns}
				items={skeletonRows}
				getItemId={(item: { id: string }) => item.id}
				aria-label='Loading payments table'
			/>
		);
	}

	// Initial state: no report generated yet - show nothing
	if (reportData === null) {
		return null;
	}

	// If we have data (even empty array), check for empty state
	// This ensures empty results don't show skeleton
	if (sortedPayments.length === 0) {
		return <EmptySearchResults title='No matching results found' className='m-t-48px' />;
	}

	// Report has results - show table
	return (
		<Table
			className='w-full payments-table'
			columns={columns}
			items={paginatedPayments}
			getItemId={(item: Payment) => item.id}
			aria-label={isPending ? 'Loading payments' : 'Payments table'}
			belowTableContent={
				<Pagination
					variant='contained'
					currentPage={currentPage}
					itemsPerPage={itemsPerPage}
					totalItems={sortedPayments.length}
					itemsPerPageOptions={[10, 25, 50, 100]}
					onNavigateTo={handlePageChange}
					onItemsPerPageChange={handleItemsPerPageChange}
					aria-label='Payments pagination'
				/>
			}
		/>
	);
}

export default ReportsPaymentReconciliationTable;
